import { TournamentConditionTypeEnum, TournamentDetailsData } from 'config/types/tournament'
import { RowCenter } from 'layout/Components/Row'
import React, { useEffect, useState } from 'react'
import Flex from 'UIKit/Box/Flex'
import EmptyContent from 'UIKit/EmptyContent'
import Table from 'UIKit/Table'
import { useTable } from 'UIKit/Table/hooks'
import { TableBody } from 'UIKit/Table/Table'
import { TableCellHistory, TableHeaderValueHistory, TableRowHistory } from 'UIKit/Table/TableHistory'
import LoadingTable from 'UIKit/Table/TableHistory/LoadingTable'
import { range } from 'utils'
import { TournamentFullLeaderBoardSchema } from 'views/Tournament/config'
import { useFetchLeaderboard, useLeaderBoardUserInfo } from '../../hooks'
import EmptyRow from '../EmptyRow'
import LeaderBoardTableRow from './LeaderBoardTableRow'
import NavigationPage from './Pagination'

type LeaderBoardTableProps = {
  tournamentDetails: TournamentDetailsData
  limitRow?: number
}

const LeaderBoardFullTable: React.FC<LeaderBoardTableProps> = ({ tournamentDetails, limitRow = 10 }) => {
  const { userInfo } = useLeaderBoardUserInfo(tournamentDetails)
  const fetchLeaderBoard = useFetchLeaderboard(tournamentDetails, limitRow)
  const { rows, headers, isLoading, pagination } = useTable(TournamentFullLeaderBoardSchema, fetchLeaderBoard, {
    displayPerPage: limitRow,
  })

  const isHideUserWithMultiplierType =
    tournamentDetails?.conditionType === TournamentConditionTypeEnum.Multiplier && Number(userInfo?.multiplier) === 0

  const isDisplayUserInfo =
    Number(userInfo?.wagerAmount) > 0 &&
    !isHideUserWithMultiplierType &&
    !rows.find((item) => item.original.userCode === userInfo.userCode)

  const renderSkeleton = () => {
    const numberOfSkeleton = isDisplayUserInfo ? limitRow - Number(rows?.length) - 1 : limitRow - Number(rows?.length)
    return range(0, numberOfSkeleton).map((item, index) => <EmptyRow key={index} />)
  }

  const [userData, setUserData] = useState(null)
  const userRow = rows?.find((item) => item.original?.userCode === userInfo?.userCode)?.original

  useEffect(() => {
    if (userRow) {
      setUserData(userRow)
    }
  }, [userRow])

  const userDisplay = userData || userInfo

  return (
    <>
      <Table>
        <thead>
          <tr>
            {headers
              .filter((header) => {
                if (header.name === 'multiplier') {
                  if (tournamentDetails?.conditionType === TournamentConditionTypeEnum.Multiplier) return header
                  return null
                }
                if (header.name === 'wagerAmount') {
                  if (tournamentDetails?.conditionType === TournamentConditionTypeEnum.Wager) return header
                  return null
                }
                return header
              })
              .filter((item) => item)
              .map((header) => (
                <TableHeaderValueHistory
                  key={header.name}
                  width={header.width}
                  label={header.label}
                  fontSize="12px !important"
                  fontWeight={600}
                />
              ))}
          </tr>
        </thead>

        <TableBody>
          {isLoading ? (
            <>
              <TableRowHistory>
                <TableCellHistory
                  style={{
                    position: 'relative',
                  }}
                  className="loader"
                  colSpan={100}
                  pl="0px !important"
                >
                  <LoadingTable loading={isLoading} top="0" />
                </TableCellHistory>
              </TableRowHistory>
            </>
          ) : (
            <>
              {rows.map((row) => {
                return (
                  <LeaderBoardTableRow
                    {...row.original}
                    key={`table-row-${row.id}`}
                    conditionType={tournamentDetails?.conditionType}
                    isActiveUser={row.original.userCode && userInfo?.userCode === row.original.userCode}
                  />
                )
              })}

              {rows?.length > 0 && renderSkeleton()}

              {isDisplayUserInfo && (
                <LeaderBoardTableRow {...userDisplay} conditionType={tournamentDetails?.conditionType} isActiveUser />
              )}
            </>
          )}
        </TableBody>
      </Table>

      {rows?.length === 0 && !isLoading && (
        <RowCenter flex={1}>
          <EmptyContent content="No player has participated yet" py={3} justifyContent="center" />
        </RowCenter>
      )}

      <Flex flex={1} alignItems="flex-end" mt={24} justifyContent="center">
        <NavigationPage pagination={pagination} />
      </Flex>
    </>
  )
}

export default LeaderBoardFullTable
